import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';

import { cancelAppointment, getBookingAppointments } from '@/controller/appointment';
import type { IAppointment } from '@/controller/appointment';
// Import Tailwind components instead of Antd
import { Empty, Modal, Pagination, Select, Spinner, TextArea, toast } from '@/shared/components/UI';
import { LIST_EMPTY_PLACEHOLDER } from '@/shared/constants';

interface IconProps {
    className?: string;
    onClick?: () => void;
    title?: string;
}

// Custom icons to replace Antd icons
const SearchIcon: React.FC = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
    </svg>
);

const PhoneIcon: React.FC<IconProps> = ({ className, onClick, title }) => (
    <svg
        className={className}
        onClick={onClick}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        style={{ width: '16px', height: '16px' }}
    >
        {title && <title>{title}</title>}
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
        />
    </svg>
);

const LocationIcon: React.FC<IconProps> = ({ className, onClick, title }) => (
    <svg
        className={className}
        onClick={onClick}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        style={{ width: '16px', height: '16px' }}
    >
        {title && <title>{title}</title>}
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
        />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
);

const CloseIcon: React.FC<IconProps> = ({ className, onClick, title }) => (
    <svg
        className={className}
        onClick={onClick}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        style={{ width: '16px', height: '16px' }}
    >
        {title && <title>{title}</title>}
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const limit = 20;

interface PageSetting {
    total?: number;
    current_page?: number;
    last_page?: number;
}

const ViewBookingAppointment: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [data, setData] = useState<IAppointment[]>([]);
    const [pageSetting, setPageSetting] = useState<PageSetting>({});
    const [page, setPage] = useState<number>(1);
    const [activeTab, setActiveTab] = useState<string>('upcoming');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
    const [viewerOpen, setViewerOpen] = useState<boolean>(false);
    const [viewerImg, setViewerImg] = useState<string | null>(null);

    // Cancellation reasons
    interface CancellationReason {
        name: string;
        value: string;
    }
    const cancellationReasons: CancellationReason[] = [
        { name: 'Product issue resolved', value: 'product_issue_resolved' },
        { name: 'Schedule conflict', value: 'schedule_conflict' },
        { name: 'Personal emergency', value: 'personal_emergency' },
        { name: 'Changed mind', value: 'changed_mind' },
        { name: 'Service center issues', value: 'service_center_issue' },
        { name: 'Weather conditions', value: 'weather_condition' },
        { name: 'Health reasons', value: 'health_reason' },
        { name: 'Other', value: 'other' },
    ];

    // Debounce search term to reduce API calls
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 1000);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        const controller = new AbortController();
        const getBookingAppointmentList = async (type = 'upcoming', filter = '') => {
            setLoading(true);
            try {
                const ret = await getBookingAppointments(type, page, limit, filter, controller.signal);
                if (ret.status === 200) {
                    setPageSetting(ret.data?.meta === undefined ? {} : ret.data?.meta);
                    setData(Array.isArray(ret.data?.data) ? ret.data?.data : []);
                    setLoading(false);
                } else if (ret.status !== 401) {
                    toast.error(ret.data?.__message || 'Error loading appointments');
                }
            } catch (e) {}
            setLoading(false);
        };

        getBookingAppointmentList(activeTab, debouncedSearchTerm);

        return () => {
            controller.abort();
        };
    }, [page, activeTab, debouncedSearchTerm]);

    // Reset to page 1 when filters change
    useEffect(() => {
        if (page !== 1) {
            setPage(1);
        }
    }, [activeTab, debouncedSearchTerm]);

    const chgPage = (e: number): void => {
        setPage(e);
    };

    const getStatusColor = (status: string | { value: string; label: string }): string => {
        const val = typeof status === 'object' ? status?.value : status;
        switch (val?.toLowerCase()) {
            case 'completed':
                return 'bg-[#e9ecf1] text-[#101319]';
            case 'confirmed':
                return 'bg-[#d4edda] text-[#155724]';
            case 'pending':
                return 'bg-[#fff3cd] text-[#856404]';
            case 'cancelled':
                return 'bg-[#f8d7da] text-[#721c24]';
            default:
                return 'bg-[#e9ecf1] text-[#101319]';
        }
    };

    const handleImageClick = (imgUrl: string | null): void => {
        if (imgUrl) {
            setViewerImg(imgUrl);
            setViewerOpen(true);
        }
    };

    const handlePhoneCall = (appointment: IAppointment): void => {
        const phoneObj = appointment.service_center?.phone;
        if (phoneObj && phoneObj.no) {
            const phoneStr = `${phoneObj.prefix || ''}${phoneObj.no}`;
            window.location.href = `tel:${phoneStr}`;
        } else {
            toast.warning('Phone number not available for this service center');
        }
    };

    const handleNavigation = (appointment: IAppointment): void => {
        const coordinate = appointment.service_center?.coordinate;
        if (coordinate?.latitude && coordinate?.longitude) {
            const { latitude, longitude } = coordinate;
            const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
            window.open(mapsUrl, '_blank');
        } else {
            toast.warning('Location coordinates not available for this service center');
        }
    };

    const handleCancelAppointment = (appointment: IAppointment): void => {
        let cancellationReason = '';
        let cancellationRemark = '';

        const modalContent = (
            <div className="space-y-6">
                <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-[#121317] text-base leading-normal">
                        Are you sure you want to cancel the appointment for{' '}
                        <strong>{appointment.product_info?.product?.name}</strong> on{' '}
                        <strong>{dayjs(appointment.date).format('YYYY-MM-DD')}</strong> at{' '}
                        <strong>{appointment.start_time}</strong>?
                    </p>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="block text-[#121317] text-base font-medium pb-2">
                            Reason for Cancellation <span className="text-red-500 ml-1">*</span>
                        </label>
                        <Select
                            options={cancellationReasons.map((reason) => ({
                                value: reason.value,
                                title: reason.name,
                            }))}
                            placeholder="Please select a reason"
                            className="w-full"
                            onChange={(value: string) => {
                                cancellationReason = value;
                            }}
                        />
                    </div>

                    <div>
                        <label className="block text-[#121317] text-base font-medium pb-2">
                            Additional Comments (Optional)
                        </label>
                        <TextArea
                            placeholder="Please provide any additional details..."
                            rows={3}
                            value={cancellationRemark}
                            onBlur={() => {}}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                cancellationRemark = e.target.value;
                            }}
                            maxLength={500}
                            showCount
                        />
                    </div>
                </div>
            </div>
        );

        Modal.confirm({
            title: 'Cancel Appointment',
            content: modalContent,
            okText: 'Cancel Appointment',
            okType: 'danger',
            cancelText: 'Keep Appointment',
            onCancel: () => {},
            onOk: async () => {
                if (!cancellationReason) {
                    toast.error('Please select a reason for cancellation');
                    return Promise.reject();
                }

                try {
                    const response = await cancelAppointment(appointment.id, {
                        reason: cancellationReason,
                        remark: cancellationRemark,
                    });

                    if (response.status === 200) {
                        toast.success('Appointment cancelled successfully');
                        const ret = await getBookingAppointments(activeTab, page, limit, debouncedSearchTerm);
                        if (ret.status === 200) {
                            setData(Array.isArray(ret.data?.data) ? ret.data?.data : []);
                        }
                    } else if (response.status !== 401) {
                        toast.error('Failed to cancel appointment');
                    }
                } catch (error) {
                    toast.error('An error occurred while cancelling the appointment');
                }
            },
        });
    };

    const canCancelAppointment = (appointment: IAppointment): boolean => {
        const appointmentDate = dayjs(appointment.date);
        const currentDate = dayjs();
        const isUpcoming = appointmentDate.isAfter(currentDate) || appointmentDate.isSame(currentDate, 'day');
        const cancellableStatuses = ['confirmed'];
        const statusValue = typeof appointment.status === 'object' ? appointment.status?.value : appointment.status;
        return isUpcoming && cancellableStatuses.includes(statusValue?.toLowerCase() || '');
    };

    const renderMobileCard = (appointment: IAppointment) => (
        <div key={appointment.id} className="bg-white border border-[#d4d9e3] rounded-xl p-4">
            <div className="flex items-start gap-3">
                <div className="flex-1 space-y-3">
                    <div className="flex justify-between items-start">
                        {appointment.product_info?.product?.images_url ? (
                            <img
                                src={appointment.product_info?.product?.images_url}
                                alt={appointment.product_info?.product?.name || 'Product'}
                                className="mr-3 w-14 h-14 rounded object-cover bg-gray-100 border border-gray-200 flex-shrink-0 cursor-pointer"
                                onClick={() => handleImageClick(appointment.product_info?.product?.images_url)}
                            />
                        ) : (
                            <div className="mr-3 p-1 w-14 h-14 rounded bg-gray-100 border border-gray-200 flex items-center text-center justify-center text-gray-400 text-xs flex-shrink-0">
                                No Image
                            </div>
                        )}
                        <div className="flex-1">
                            <h3 className="text-[#101319] text-base font-medium leading-normal">
                                {appointment.product_info?.product?.name}
                            </h3>
                            <p className="text-[#5b6b8b] text-sm">{appointment.product_info?.serial_number}</p>
                        </div>
                        {appointment.status?.value && (
                            <button
                                className={`flex min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-full h-8 px-4 ${getStatusColor(
                                    appointment.status,
                                )} text-sm font-medium leading-normal`}
                            >
                                <span className="truncate">
                                    {appointment.status?.label ?? appointment.status?.value}
                                </span>
                            </button>
                        )}
                    </div>
                    {appointment.status?.value === 'cancelled' && appointment.cancel_reason && (
                        <div className="mt-2 p-2 bg-red-50 rounded-lg border border-red-100">
                            <p className="text-xs text-red-700 font-medium">
                                Cancelled by: {appointment.cancel_reason.cancelled_by?.name || 'Staff'}
                            </p>
                            <p className="text-xs text-red-600 mt-0.5">
                                Reason:{' '}
                                {appointment.cancel_reason.reason?.label ||
                                    appointment.cancel_reason.reason?.value ||
                                    'N/A'}
                            </p>
                            {appointment.cancel_reason.remark && (
                                <p className="text-xs text-red-500 italic mt-0.5">
                                    "{appointment.cancel_reason.remark}"
                                </p>
                            )}
                        </div>
                    )}
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <span className="text-[#5b6b8b] text-sm">Date:</span>
                            <span className="text-[#101319] text-sm font-medium">
                                {appointment.date
                                    ? dayjs(appointment.date).format('YYYY-MM-DD')
                                    : LIST_EMPTY_PLACEHOLDER}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-[#5b6b8b] text-sm">Time:</span>
                            <span className="text-[#101319] text-sm font-medium">
                                {appointment.start_time || LIST_EMPTY_PLACEHOLDER}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-[#5b6b8b] text-sm">Service Center:</span>
                            <span className="text-[#101319] text-sm font-medium">
                                {appointment.service_center?.name || LIST_EMPTY_PLACEHOLDER}
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-[#5b6b8b] text-sm">Actions:</span>
                            <div className="flex gap-2">
                                <PhoneIcon
                                    className={`text-sm cursor-pointer ${
                                        appointment.service_center?.phone?.no
                                            ? 'text-[#5b6b8b] hover:text-[#101319]'
                                            : 'text-gray-300 cursor-not-allowed'
                                    }`}
                                    onClick={() => appointment.service_center?.phone?.no && handlePhoneCall(appointment)}
                                    title={
                                        appointment.service_center?.phone?.no
                                            ? `${appointment.service_center.phone.prefix || ''}${appointment.service_center.phone.no}`
                                            : 'Phone not available'
                                    }
                                />
                                <LocationIcon
                                    className={`text-sm cursor-pointer ${
                                        appointment.service_center?.coordinate?.latitude
                                            ? 'text-[#5b6b8b] hover:text-[#101319]'
                                            : 'text-gray-300 cursor-not-allowed'
                                    }`}
                                    onClick={() =>
                                        appointment.service_center?.coordinate?.latitude &&
                                        handleNavigation(appointment)
                                    }
                                    title={
                                        appointment.service_center?.coordinate?.latitude
                                            ? 'Navigate to location'
                                            : 'Location not available'
                                    }
                                />
                                {canCancelAppointment(appointment) && (
                                    <CloseIcon
                                        className="text-sm cursor-pointer text-red-500 hover:text-red-700"
                                        onClick={() => handleCancelAppointment(appointment)}
                                        title="Cancel appointment"
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <div className="relative flex size-full min-h-screen flex-col bg-white">
                <div className="layout-container flex h-full grow flex-col">
                    <div className="px-4 sm:px-8 md:px-16 lg:px-32 xl:px-40 flex flex-1 justify-center py-5">
                        <div className="layout-content-container flex flex-col max-w-[960px] flex-1">
                            <div className="flex flex-wrap justify-between gap-3 p-4">
                                <div className="flex min-w-72 flex-col gap-3">
                                    <p className="text-[#111218] tracking-light text-2xl sm:text-3xl lg:text-[32px] font-bold leading-tight">
                                        Appointments
                                    </p>
                                    <p className="text-[#5f678c] text-sm font-normal leading-normal">
                                        View your upcoming and past appointments
                                    </p>
                                </div>
                            </div>

                            <div className="pb-3">
                                <div className="flex border-b border-[#d4d9e3] px-4 gap-8">
                                    <button
                                        onClick={() => setActiveTab('upcoming')}
                                        className={`flex flex-col items-center justify-center border-b-[3px] pb-[13px] pt-4 ${
                                            activeTab === 'upcoming'
                                                ? 'border-b-[#2563eb] text-[#101319]'
                                                : 'border-b-transparent text-[#5b6b8b]'
                                        }`}
                                    >
                                        <p
                                            className={`text-sm font-bold leading-normal tracking-[0.015em] ${
                                                activeTab === 'upcoming' ? 'text-[#2563eb]' : 'text-[#5b6b8b]'
                                            }`}
                                        >
                                            Upcoming
                                        </p>
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('past')}
                                        className={`flex flex-col items-center justify-center border-b-[3px] pb-[13px] pt-4 ${
                                            activeTab === 'past'
                                                ? 'border-b-[#2563eb] text-[#101319]'
                                                : 'border-b-transparent text-[#5b6b8b]'
                                        }`}
                                    >
                                        <p
                                            className={`text-sm font-bold leading-normal tracking-[0.015em] ${
                                                activeTab === 'past' ? 'text-[#2563eb]' : 'text-[#5b6b8b]'
                                            }`}
                                        >
                                            Past
                                        </p>
                                    </button>
                                </div>
                            </div>

                            <div className="px-4 py-3">
                                <label className="flex flex-col min-w-40 h-12 w-full">
                                    <div className="flex w-full flex-1 items-stretch rounded-xl h-full">
                                        <div className="text-[#5b6b8b] flex border-none bg-[#e9ecf1] items-center justify-center pl-4 rounded-l-xl border-r-0">
                                            <SearchIcon />
                                        </div>
                                        <input
                                            placeholder="Search by product"
                                            className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-xl text-[#101319] focus:outline-0 focus:ring-0 border-none bg-[#e9ecf1] focus:border-none h-full placeholder:text-[#5b6b8b] px-4 rounded-l-none border-l-0 pl-2 text-base font-normal leading-normal"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </label>
                            </div>

                            <div className="px-4 py-3">
                                <Spinner spinning={loading}>
                                    <div className="hidden md:flex overflow-hidden rounded-xl border border-[#d4d9e3] bg-gray-50">
                                        <table className="flex-1">
                                            <thead>
                                                <tr className="bg-gray-50">
                                                    <th className="px-4 py-3 text-left text-[#101319] text-sm font-medium leading-normal">
                                                        Image
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-[#101319] text-sm font-medium leading-normal">
                                                        Date
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-[#101319] text-sm font-medium leading-normal">
                                                        Time
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-[#101319] text-sm font-medium leading-normal">
                                                        Product
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-[#101319] text-sm font-medium leading-normal">
                                                        Service Center
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-[#101319] text-sm font-medium leading-normal">
                                                        Status
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-[#5b6b8b] text-sm font-medium leading-normal">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.length > 0 ? (
                                                    data.map((appointment, index) => (
                                                        <tr
                                                            key={appointment.id || index}
                                                            className="border-t border-t-[#d4d9e3]"
                                                        >
                                                            <td className="h-[72px] px-4 py-2">
                                                                {appointment.product_info?.product?.images_url ? (
                                                                    <img
                                                                        src={
                                                                            appointment.product_info?.product
                                                                                ?.images_url
                                                                        }
                                                                        alt={
                                                                            appointment.product_info?.product?.name ||
                                                                            'Product'
                                                                        }
                                                                        className="w-12 h-12 rounded object-cover bg-gray-100 border border-gray-200 cursor-pointer"
                                                                        style={{ minWidth: 48, minHeight: 48 }}
                                                                        onClick={() =>
                                                                            handleImageClick(
                                                                                appointment.product_info?.product
                                                                                    ?.images_url,
                                                                            )
                                                                        }
                                                                    />
                                                                ) : (
                                                                    <div className="w-12 h-12 rounded bg-gray-100 border border-gray-200 flex items-center justify-center text-center text-gray-400 text-xs">
                                                                        No Image
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className="h-[72px] px-4 py-2 text-[#5b6b8b] text-sm font-normal leading-normal">
                                                                {appointment.date
                                                                    ? dayjs(appointment.date).format('YYYY-MM-DD')
                                                                    : LIST_EMPTY_PLACEHOLDER}
                                                            </td>
                                                            <td className="h-[72px] px-4 py-2 text-[#5b6b8b] text-sm font-normal leading-normal">
                                                                {appointment.start_time || LIST_EMPTY_PLACEHOLDER}
                                                            </td>
                                                            <td className="h-[72px] px-4 py-2 text-[#5b6b8b] text-sm font-normal leading-normal">
                                                                {!appointment.product_info?.product?.name &&
                                                                !appointment.product_info?.serial_number ? (
                                                                    LIST_EMPTY_PLACEHOLDER
                                                                ) : (
                                                                    <>
                                                                        {appointment.product_info?.product?.name}
                                                                        {appointment.product_info?.product?.name &&
                                                                            appointment.product_info?.serial_number && (
                                                                                <br />
                                                                            )}
                                                                        {appointment.product_info?.serial_number}
                                                                    </>
                                                                )}
                                                            </td>
                                                            <td className="h-[72px] px-4 py-2 text-[#5b6b8b] text-sm font-normal leading-normal">
                                                                {appointment.service_center?.name ||
                                                                    LIST_EMPTY_PLACEHOLDER}
                                                            </td>
                                                            <td className="h-[72px] px-4 py-2 text-[#5b6b8b] text-sm font-normal leading-normal">
                                                                {appointment.status?.value ? (
                                                                    <button
                                                                        className={`flex min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-full h-8 px-4 ${getStatusColor(
                                                                            appointment.status,
                                                                        )} text-sm font-medium leading-normal w-full`}
                                                                    >
                                                                        <span className="truncate">
                                                                            {appointment.status?.label ??
                                                                                appointment.status?.value}
                                                                        </span>
                                                                    </button>
                                                                ) : (
                                                                    LIST_EMPTY_PLACEHOLDER
                                                                )}
                                                                {appointment.status?.value === 'cancelled' &&
                                                                    appointment.cancel_reason && (
                                                                        <div className="mt-1 text-[10px] leading-tight">
                                                                            <div className="text-red-700 font-medium">
                                                                                By:{' '}
                                                                                {appointment.cancel_reason.cancelled_by
                                                                                    ?.name || 'Staff'}
                                                                            </div>
                                                                            <div
                                                                                className="text-red-600 truncate max-w-[120px]"
                                                                                title={
                                                                                    appointment.cancel_reason.reason
                                                                                        ?.label ||
                                                                                    appointment.cancel_reason.reason
                                                                                        ?.value
                                                                                }
                                                                            >
                                                                                {appointment.cancel_reason.reason
                                                                                    ?.label ||
                                                                                    appointment.cancel_reason.reason
                                                                                        ?.value ||
                                                                                    'N/A'}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                            </td>
                                                            <td className="h-[72px] px-4 py-2 text-[#5b6b8b] text-sm font-bold leading-normal tracking-[0.015em]">
                                                                <div className="flex gap-2">
                                                                    <PhoneIcon
                                                                        className={`cursor-pointer ${
                                                                            appointment.service_center?.phone?.no
                                                                                ? 'hover:text-[#101319]'
                                                                                : 'text-gray-300 cursor-not-allowed'
                                                                        }`}
                                                                        onClick={() =>
                                                                            appointment.service_center?.phone?.no &&
                                                                            handlePhoneCall(appointment)
                                                                        }
                                                                        title={
                                                                            appointment.service_center?.phone?.no
                                                                                ? `${appointment.service_center.phone.prefix || ''}${appointment.service_center.phone.no}`
                                                                                : 'Phone not available'
                                                                        }
                                                                    />
                                                                    <LocationIcon
                                                                        className={`cursor-pointer ${
                                                                            appointment.service_center?.coordinate
                                                                                ?.latitude
                                                                                ? 'hover:text-[#101319]'
                                                                                : 'text-gray-300 cursor-not-allowed'
                                                                        }`}
                                                                        onClick={() =>
                                                                            appointment.service_center?.coordinate
                                                                                ?.latitude &&
                                                                            handleNavigation(appointment)
                                                                        }
                                                                        title={
                                                                            appointment.service_center?.coordinate
                                                                                ?.latitude
                                                                                ? 'Navigate to location'
                                                                                : 'Location not available'
                                                                        }
                                                                    />
                                                                    {canCancelAppointment(appointment) && (
                                                                        <CloseIcon
                                                                            className="cursor-pointer text-red-500 hover:text-red-700"
                                                                            onClick={() =>
                                                                                handleCancelAppointment(appointment)
                                                                            }
                                                                            title="Cancel appointment"
                                                                        />
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={7} className="h-[200px]">
                                                            <div className="flex items-center justify-center h-full">
                                                                <Empty
                                                                    image="simple"
                                                                    description={
                                                                        debouncedSearchTerm
                                                                            ? 'No appointments match your search'
                                                                            : `No ${activeTab} appointments found`
                                                                    }
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="md:hidden space-y-4">
                                        {data.length > 0 ? (
                                            data.map(renderMobileCard)
                                        ) : (
                                            <div className="text-center py-12">
                                                <Empty
                                                    image="simple"
                                                    description={
                                                        debouncedSearchTerm
                                                            ? 'No appointments match your search'
                                                            : `No ${activeTab} appointments found`
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {data.length > 0 && pageSetting.total && pageSetting.total > limit && (
                                        <div className="flex items-center justify-center mt-8">
                                            <Pagination
                                                current={page}
                                                pageSize={limit}
                                                total={pageSetting.total}
                                                onChange={chgPage}
                                                showTotal={(total: number, range: [number, number]) =>
                                                    `${range[0]}-${range[1]} of ${total} items`
                                                }
                                            />
                                        </div>
                                    )}
                                </Spinner>
                            </div>

                            <div className="pb-24" />
                        </div>
                    </div>
                </div>
            </div>
            {viewerOpen && (
                <Modal 
                    isOpen={viewerOpen} 
                    onClose={() => setViewerOpen(false)} 
                    onConfirm={() => {}}
                    onCancel={() => setViewerOpen(false)}
                    size="small" 
                    title=""
                >
                    <div className="relative">
                        <button
                            onClick={() => setViewerOpen(false)}
                            className="absolute -top-10 -right-10 z-10 p-2 rounded-full bg-white hover:bg-gray-100 border border-gray-200 shadow"
                            aria-label="Close image viewer"
                            type="button"
                        >
                            <svg
                                className="w-6 h-6 text-gray-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                        <div className="flex items-center justify-center">
                            {viewerImg && (
                                <img
                                    src={viewerImg}
                                    alt="Product"
                                    className="max-h-[70vh] max-w-full rounded-xl object-contain"
                                />
                            )}
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default ViewBookingAppointment;
